import { useEffect, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { settingsApi, type WooCommercePreview } from '../../services/settings';

function statusLabel(status: WooCommercePreview['items'][number]['status']): string {
  if (status === 'matched') return 'Matchad';
  if (status === 'missing_sku') return 'Saknar SKU';
  return 'Ny';
}

function statusClass(status: WooCommercePreview['items'][number]['status']): string {
  if (status === 'matched') return 'text-green-700 bg-green-50';
  if (status === 'missing_sku') return 'text-amber-800 bg-amber-50';
  return 'text-blue-700 bg-blue-50';
}

export function WooCommerceSettingsPage() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<WooCommercePreview | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const data = await settingsApi.getWooCommercePreview();
        if (active) {
          setPreview(data);
        }
      } catch (error) {
        if (active) {
          toast.error(error instanceof Error ? error.message : 'Kunde inte hämta WooCommerce-preview');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [toast]);

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold">WooCommerce</h2>
      <p className="text-sm text-gray-600">Förhandsvisning av publicerade artiklar från e-handeln innan någon import byggs.</p>

      {loading && !preview ? <p className="text-sm text-gray-500">Laddar preview...</p> : null}

      {preview ? (
        <>
          <div className="rounded border border-gray-200 bg-gray-50 p-3 space-y-2">
            <div className="text-sm"><span className="text-gray-500">Butik:</span> {preview.storeUrl}</div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <div className="rounded border border-gray-200 bg-white p-2">
                <div className="text-xs uppercase tracking-wide text-gray-500">Woo</div>
                <div className="text-lg font-semibold">{preview.totals.wooProducts}</div>
              </div>
              <div className="rounded border border-gray-200 bg-white p-2">
                <div className="text-xs uppercase tracking-wide text-gray-500">Matchade</div>
                <div className="text-lg font-semibold">{preview.totals.matched}</div>
              </div>
              <div className="rounded border border-gray-200 bg-white p-2">
                <div className="text-xs uppercase tracking-wide text-gray-500">Nya</div>
                <div className="text-lg font-semibold">{preview.totals.new}</div>
              </div>
              <div className="rounded border border-gray-200 bg-white p-2">
                <div className="text-xs uppercase tracking-wide text-gray-500">Saknar SKU</div>
                <div className="text-lg font-semibold">{preview.totals.missingSku}</div>
              </div>
            </div>
          </div>

          <div className="rounded border border-gray-200 overflow-hidden">
            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 18rem)' }}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Status</th>
                    <th className="px-2 py-1.5 text-left">Bild</th>
                    <th className="px-2 py-1.5 text-left">Woo-ID</th>
                    <th className="px-2 py-1.5 text-left">SKU</th>
                    <th className="px-2 py-1.5 text-left">Namn</th>
                    <th className="px-2 py-1.5 text-left">Pris</th>
                    <th className="px-2 py-1.5 text-left">Lager</th>
                    <th className="px-2 py-1.5 text-left">Match i ApisAI</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((item) => (
                    <tr key={item.wooId} className="border-t border-gray-100">
                      <td className="px-2 py-1.5">
                        <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${statusClass(item.status)}`}>
                          {statusLabel(item.status)}
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        {item.imageUrl ? (
                          <div className="flex items-center gap-2">
                            <img
                              src={item.imageUrl}
                              alt={item.imageAlt || item.name}
                              className="h-10 w-10 rounded border border-gray-200 object-cover bg-white"
                              loading="lazy"
                              referrerPolicy="no-referrer"
                            />
                            <span className="text-xs text-gray-600">Ja</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">Nej</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">{item.wooId}</td>
                      <td className="px-2 py-1.5">{item.sku ?? '-'}</td>
                      <td className="px-2 py-1.5">{item.name}</td>
                      <td className="px-2 py-1.5">{item.price !== '' ? item.price : '-'}</td>
                      <td className="px-2 py-1.5">{item.stockStatus !== '' ? item.stockStatus : '-'}</td>
                      <td className="px-2 py-1.5">
                        {item.matchedProductId
                          ? `${item.matchedProductSku ?? '-'} - ${item.matchedProductName ?? ''}`
                          : '-'}
                      </td>
                    </tr>
                  ))}
                  {preview.items.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-2 py-4 text-sm text-gray-500">Inga publicerade WooCommerce-artiklar hittades.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
